<template>
    <lender v-if="document.type === 'lender'" :document="document" />
    <corporate-lender v-else-if="document.type === 'lender_corporate'" :document="document" />
    <credit-card v-else-if="document.type === 'credit_card'" :document="document" />
    <page v-else-if="document.type === 'page'" :document="document" />
    <author v-else-if="document.type === 'author'" :document="document" />
    <post v-else-if="document.type === 'post'" :document="document" />
</template>
<script>
import getDocument from '@/plugins/get-document';
import Page from '@/components/pages/Page.vue';
import Author from '@/components/pages/Author.vue';
import CreditCard from '@/components/pages/card/CreditCard.vue';
import Lender from '@/components/pages/lender/Lender.vue';
import CorporateLender from '@/components/pages/lender/CorporateLender.vue';
import Post from '@/components/posts/Post.vue';

export default {
    components: {
        Lender,
        Page,
        Author,
        CreditCard,
        CorporateLender,
        Post
    },
    layout: 'site',
    async asyncData({$prismic, route, error}) {
        try {
            return await getDocument($prismic, route.path, error);
        } 
        catch (e) {
            error({statusCode: 404, message: 'Not found'});
        }
    },
    computed: {
        settings() {
            return this.$store.state.settings;
        },
        seoTitle() {
            const seoTitle = this.trim(this.document.data.seo_title);
            if (seoTitle) {
                return seoTitle;
            }
            return this.getTemplatedSeoMeta('title');
        },
        seoDescription() {
            const seoDescription = this.trim(this.document.data.seo_description);
            if (seoDescription) {
                return seoDescription;
            }
            return this.getTemplatedSeoMeta('description');
        }
    },
    methods: {
        getTemplatedSeoMeta(metaType) {
            const field = `seo_${metaType}_template`;
            if (this.document.type === 'lender') {
                const template = this.settings[`lender_${field}`];
                if (template) {
                    return this.replaceLenderShortcodes(template);
                }
            } else if (this.document.type === 'credit_card') {
                const template = this.settings[`card_${field}`];
                if (template) {
                    return this.replaceCreditCardShortcodes(template);
                }
            }
            return '';
        },
        trim(template) {
            return (template || '')
                .trim();
        },
        replaceLenderShortcodes(template) {
            const model = new this.$models.Loan(this.document);
            const shortcodes = {
                '{title}': this.document.data.title,
                '{amount}': model.amountString,
                '{maxAmount}': model.maxAmountString,
                '{minAmount}': model.minAmountString,
                '{interest}': model.interestString,
                '{minInterest}': model.minInterestPossibleString,
                '{maxInterest}': model.maxInterestPossibleString,
                '{effectiveInterest}': model.effectiveInterestString,
                '{minEffectiveInterest}': model.minEffectiveInterestPossibleString,
                '{maxEffectiveInterest}': model.maxEffectiveInterestPossibleString,
                '{duration}': model.getDurationString(this.$translate('month_short', 'mån'), this.$translate('year', 'år')),
                '{creditCheck}': this.$getCreditCheckString(model.creditCheck)
            };

            return this.replaceShortcodes(template, shortcodes);
        },
        replaceCreditCardShortcodes(template) {
            const model = new this.$models.CreditCard(this.document);
            const shortcodes = {
                '{title}': this.document.data.title,
                '{maxCredit}': model.getMaxCreditString(),
                '{interest}': model.interestString,
                '{effectiveInterest}': model.effectiveInterestString,
                '{yearlyCost}': model.yearlyCostString,
                '{interestFreeDays}': model.getInterestFreeDaysString()
            };

            return this.replaceShortcodes(template, shortcodes);
        },
        replaceShortcodes(template, shortcodes) {
            let result = template;
            for (const [shortcode, value] of Object.entries(shortcodes)) {
                result = result.replaceAll(shortcode, value);
            }
            return result;
        }
    },
    head() {
        return {
            htmlAttrs: {
                lang: this.document.data.is_english_page ? 'en' : process.env.HTML_LANG || undefined
            },
            title: this.seoTitle,
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.seoDescription
                }
            ]
                .concat(this.document.data.is_landing_page === true ? [{ name: 'robots', content: 'noindex' }] : []),
            link: [
                {
                    rel: 'canonical',
                    href: this.$prismic.linkResolver(this.document, true)
                },
            ]
                .concat(this.hreflangs)
        };
    }
};
</script>
